/**
 * Preferences questionnaire page-flow — handlers + progress facade.
 *
 * Belongs here: back/next/answer handlers + progress bundle for questionnaire pages.
 * Does not belong here: answer store, API fetch, or pure flow rules.
 *
 * Intern: answerQuestion → switch on result; allDone → leaveQuestionnaire(leave).
 */
import { useMemo } from 'react'
import { SlideDirection } from '../../shared/transitions/DirectionalSlideSwap'
import { usePreferencesContext } from './PreferencesProvider'
import { PreferencesAnswerResultType } from './usePreferencesQuestionnaire'
import { PreferencesQuestionnaireScope } from '../setup/types'
import {
  getPreferencesBackTarget,
  PreferencesBackTargetType
} from './flowRules'

/**
 * Handlers for one preferences questionnaire route (full flow or single-type edit).
 */
export function usePreferencesQuestionHandlers(scope: PreferencesQuestionnaireScope) {
  const {
    draftAnswers,
    answerQuestion,
    finishFromDraftAnswers,
    leaveQuestionnaire,
    cancelAnswering,
    preferenceTypes,
    preferenceTypeIndex,
    questionIndex,
    currentPreferenceType,
    currentQuestion,
    visibleQuestionsForCurrentType,
    goToQuestion,
    questionnaireMap
  } = usePreferencesContext()

  const isSingleType = scope === PreferencesQuestionnaireScope.Single
  const currentQuestionResolved = visibleQuestionsForCurrentType[questionIndex] ?? currentQuestion
  const visibleCountInCurrentType = visibleQuestionsForCurrentType.length
  const isLastInType =
    visibleCountInCurrentType > 0 && questionIndex >= visibleCountInCurrentType - 1
  const isLastPreferenceType = preferenceTypeIndex >= preferenceTypes.length - 1
  const isComplete = isSingleType ? isLastInType : isLastPreferenceType && isLastInType

  function handleBack() {
    // Single-type edit: step back within the type, or cancel out of the edit route
    if (isSingleType) {
      if (questionIndex > 0) {
        goToQuestion(preferenceTypeIndex, questionIndex - 1, SlideDirection.Back)
        return
      }
      cancelAnswering()
      return
    }

    const backTarget = getPreferencesBackTarget(
      questionIndex,
      preferenceTypeIndex,
      preferenceTypes,
      questionnaireMap,
      draftAnswers
    )
    if (backTarget.type === PreferencesBackTargetType.BrowserBack) {
      cancelAnswering()
      return
    }
    goToQuestion(backTarget.preferenceTypeIndex, backTarget.questionIndex, SlideDirection.Back)
  }

  /**
   * Next button (mainly multi-select). When the last step is complete,
   * commit draft → Leave and leave — no new AnswerQuestion needed.
   */
  function handleNext() {
    if (isComplete) {
      leaveQuestionnaire(finishFromDraftAnswers())
      return
    }
    if (isSingleType) {
      goToQuestion(preferenceTypeIndex, questionIndex + 1, SlideDirection.Forward)
      return
    }
    if (questionIndex < visibleCountInCurrentType - 1) {
      goToQuestion(preferenceTypeIndex, questionIndex + 1, SlideDirection.Forward)
      return
    }
    // Last question in type but more types remain — cursor-only advance (draft already has picks)
    if (preferenceTypeIndex < preferenceTypes.length - 1) {
      goToQuestion(preferenceTypeIndex + 1, 0, SlideDirection.Forward)
      return
    }
    leaveQuestionnaire(finishFromDraftAnswers())
  }

  /**
   * Single-select confirm. answerQuestion owns rules + commit;
   * we only leave when result is allDone (prefs has no stage Rive).
   */
  function handleAnswerAndAdvance(questionId: string, value: string | number) {
    if (!currentQuestionResolved || !currentPreferenceType) return

    const result = answerQuestion(questionId, value, {
      isSingleTypeEdit: isSingleType
    })

    // PreferenceTypeDone / NextQuestion: stay on questionnaire (prefs has no stage Rive)
    if (result.type === PreferencesAnswerResultType.AllDone) {
      leaveQuestionnaire(result.leave)
    }
  }

  return { handleBack, handleNext, handleAnswerAndAdvance }
}

/**
 * Page facade — handlers + progress for preferences questionnaire pages.
 */
export function usePreferencesQuestionnaireFlow(scope: PreferencesQuestionnaireScope) {
  const handlers = usePreferencesQuestionHandlers(scope)
  const {
    preferenceTypes,
    preferenceTypeIndex,
    questionIndex,
    currentPreferenceType,
    currentQuestion,
    visibleQuestionsForCurrentType,
    slideDirection
  } = usePreferencesContext()

  const isSingleType = scope === PreferencesQuestionnaireScope.Single
  const currentQuestionResolved =
    visibleQuestionsForCurrentType[questionIndex] ?? currentQuestion
  const visibleCountInCurrentType = visibleQuestionsForCurrentType.length
  const isLastInType =
    visibleCountInCurrentType > 0 && questionIndex >= visibleCountInCurrentType - 1
  const isLastPreferenceType = preferenceTypeIndex >= preferenceTypes.length - 1
  const isComplete = isSingleType ? isLastInType : isLastPreferenceType && isLastInType

  const progress = useMemo(
    () => ({
      isSingleType,
      preferenceTypes,
      preferenceTypeIndex,
      questionIndex,
      currentPreferenceType,
      currentQuestionResolved,
      currentQuestionId: currentQuestionResolved?.id ?? null,
      visibleQuestions: visibleQuestionsForCurrentType,
      visibleCountInCurrentType,
      isComplete,
      slideDirection
    }),
    [
      isSingleType,
      preferenceTypes,
      preferenceTypeIndex,
      questionIndex,
      currentPreferenceType,
      currentQuestionResolved,
      visibleQuestionsForCurrentType,
      visibleCountInCurrentType,
      isComplete,
      slideDirection
    ]
  )

  return { progress, handlers }
}
